import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export type OverviewFitContextValue = {
	/** Each overview sheet reports the largest font size at which its own body fits. */
	reportFit: (key: string, px: number) => void;
	/** Smallest fit reported by any sheet, so every overview sheet renders at one size. */
	appliedPx: number | null;
};

const OverviewFitContext = createContext<OverviewFitContextValue | null>(null);

export type OverviewFitProviderProps = {
	children: React.ReactNode;
};

/**
 * Keeps a split kitchen-overview typographically uniform. Each sheet shrink-to-fits
 * independently, so a dense first sheet would render smaller than a sparse continuation —
 * visibly mismatched type across two sheets of the same section. Sheets report their own
 * natural fit here and all of them render at the minimum.
 */
export const OverviewFitProvider = (props: OverviewFitProviderProps): React.ReactElement => {
	const { children } = props;
	const fitsRef = useRef<Map<string, number>>(new Map());
	const [appliedPx, setAppliedPx] = useState<number | null>(null);

	const reportFit = useCallback((key: string, px: number): void => {
		if (fitsRef.current.get(key) === px) return;
		fitsRef.current.set(key, px);
		setAppliedPx(Math.min(...fitsRef.current.values()));
	}, []);

	const value = useMemo((): OverviewFitContextValue => ({ reportFit, appliedPx }), [reportFit, appliedPx]);

	return (
		<OverviewFitContext.Provider value={value}>
			{children}
		</OverviewFitContext.Provider>
	);
};

// eslint-disable-next-line no-empty-function
const noReport = (): void => {};

export const useOverviewFit = (): OverviewFitContextValue => {
	return useContext(OverviewFitContext) ?? { reportFit: noReport, appliedPx: null };
};
